import {
  Badge,
  Box,
  Button,
  Flex,
  Modal,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useCommentKYC } from "../../api/hooks/kyc/useCommentKYC";
import { useKYCResultDetail } from "../../api/hooks/kyc/useKYCResultDetail";
import { useTrack } from "../../api/hooks/user/useTrack";
import SvgDownload from "../../components/Icons/Download";
import Layout from "../../components/Layout/Layout";
import Preloader from "../../components/Preloader/Preloader";
import { ActivityTrackerTypes } from "../../graphql/generated";
import { useCurrentUserContext } from "../../providers/CurrentUserProvider.tsx";
import { useUpdateKycAccurate } from "./hooks/useUpdateKycAccurate";
import { useStyles } from "./KYCDetail.styles";
import {
  CreditInfo,
  ForgeryResult,
  KYCComment,
  KYCResultData,
  NegativeInfo,
} from "./types";

// Type guard functions
const isKYCResultData = (item: unknown): item is KYCResultData => {
  return (
    typeof item === "object" &&
    item !== null &&
    "company_identity" in item &&
    "management" in item
  );
};

const isCreditInfo = (item: unknown): item is CreditInfo => {
  return (
    typeof item === "object" &&
    item !== null &&
    "creditHistory" in item &&
    "tradeName" in item
  );
};

const isNegativeInfo = (item: unknown): item is NegativeInfo => {
  return (
    typeof item === "object" &&
    item !== null &&
    "negative_news" in item &&
    "entity" in item
  );
};

const isForgeryResult = (item: unknown): item is ForgeryResult => {
  return typeof item === "object" && item !== null && "forgery" in item;
};

const KYCResultPage = () => {
  const { classes, cx } = useStyles();
  const [, track] = useTrack();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const customerId = searchParams.get("customerId");
  const [newComment, setNewComment] = useState("");
  const { user: currentUser } = useCurrentUserContext();

  const [showNotAccurateModal, setShowNotAccurateModal] = useState(false);
  const [showConfirmAccurateModal, setShowConfirmAccurateModal] =
    useState(false);
  const [notAccurateComment, setNotAccurateComment] = useState("");

  const [{ data, fetching }, refetch] = useKYCResultDetail({
    id: Number(id),
    pause: !id,
  });

  const { addComment, fetching: addingComment } = useCommentKYC();
  const { updateAccurate, fetching: updatingAccurate } = useUpdateKycAccurate();

  // Get accurate status from API
  const isAccurate = data?.kycResult?.accurate;
  const isVerified = isAccurate !== null && isAccurate !== undefined;

  // Parse the result data
  const resultData = data?.kycResult?.result as unknown[] | undefined;

  const companyData = resultData?.find(isKYCResultData);
  const creditInfoList = resultData?.filter(isCreditInfo) ?? [];
  const negativeInfo = resultData?.find(isNegativeInfo);
  const forgeryResult = resultData?.find(isForgeryResult);

  // Comments from API
  const [comments, setComments] = useState<KYCComment[]>([]);

  useEffect(() => {
    if (data?.kycResult?.comment) {
      const apiComments = data.kycResult.comment as unknown as KYCComment[];
      setComments(apiComments);
    }
  }, [data]);

  const scrollToFeedback = () => {
    const feedbackSection = document.getElementById("feedback-section");
    if (feedbackSection) {
      feedbackSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleDownloadPDF = () => {
    track({
      input: {
        eventType: ActivityTrackerTypes.ClickSavePdf,
        metadata: {
          kyc_id: Number(id),
          customer_id: Number(customerId),
        },
      },
    });

    window.print();
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !id) return;

    if (isAccurate === true) {
      notifications.show({
        title: "Cannot Add Comment",
        message: "Comments are disabled when the result is marked as accurate",
        color: "orange",
      });
      return;
    }

    try {
      const result = await addComment(Number(id), newComment);

      if (result.data?.comment) {
        setComments([
          ...comments,
          {
            id: Date.now(),
            commenter: currentUser?.name || "Unknown User",
            comment: newComment,
            createdAt: new Date().toISOString(),
          },
        ]);
        setNewComment("");
        notifications.show({
          title: "Success",
          message: "Comment added successfully",
          color: "green",
        });
      } else if (result.error) {
        notifications.show({
          title: "Error",
          message: result.error.message || "Failed to add comment",
          color: "red",
        });
      }
    } catch (err) {
      notifications.show({
        title: "Error",
        message: "An unexpected error occurred",
        color: "red",
      });
    }
  };

  const handleYesClick = () => {
    setShowConfirmAccurateModal(true);
  };

  const handleNoClick = () => {
    setShowNotAccurateModal(true);
  };

  const handleConfirmAccurate = async () => {
    if (!id) return;

    try {
      const result = await updateAccurate(Number(id), true);

      if (result.data?.updateKycAccurate) {
        notifications.show({
          title: "Success",
          message: "Result marked as accurate",
          color: "green",
        });

        track({
          input: {
            eventType: ActivityTrackerTypes.AccurateKyc,
            metadata: {
              kyc_id: Number(id),
              customer_id: Number(customerId),
              accurate: true,
            },
          },
        });

        refetch({ requestPolicy: "network-only" });
        setShowConfirmAccurateModal(false);
      } else if (result.error) {
        notifications.show({
          title: "Error",
          message: result.error.message || "Failed to update status",
          color: "red",
        });
      }
    } catch (err) {
      notifications.show({
        title: "Error",
        message: "An unexpected error occurred",
        color: "red",
      });
    }
  };

  const handleSubmitNotAccurate = async () => {
    if (!id || !notAccurateComment.trim()) return;

    try {
      // First mark as not accurate
      const updateResult = await updateAccurate(Number(id), false);

      if (updateResult.error) {
        notifications.show({
          title: "Error",
          message: updateResult.error.message || "Failed to update status",
          color: "red",
        });
        return;
      }

      // Then add the comment
      const commentResult = await addComment(Number(id), notAccurateComment);

      if (commentResult.data?.comment) {
        setComments([
          ...comments,
          {
            id: Date.now(),
            commenter: currentUser?.name || "Unknown User",
            comment: notAccurateComment,
            createdAt: new Date().toISOString(),
          },
        ]);

        notifications.show({
          title: "Success",
          message: "Result marked for review with comment",
          color: "green",
        });

        track({
          input: {
            eventType: ActivityTrackerTypes.AccurateKyc,
            metadata: {
              kyc_id: Number(id),
              customer_id: Number(customerId),
              accurate: false,
            },
          },
        });

        refetch({ requestPolicy: "network-only" });
        setShowNotAccurateModal(false);
        setNotAccurateComment("");
      } else if (commentResult.error) {
        notifications.show({
          title: "Error",
          message: commentResult.error.message || "Failed to add comment",
          color: "red",
        });
      }
    } catch (err) {
      notifications.show({
        title: "Error",
        message: "An unexpected error occurred",
        color: "red",
      });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (value: number | string) => {
    const num = typeof value === "string" ? parseFloat(value) : value;
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(num);
  };

  if (!companyData) {
    return (
      <Layout>
        <Preloader loading={fetching} />
        <Flex align="center" justify="center" style={{ height: "50vh" }}>
          <Text>No KYC result data available</Text>
        </Flex>
      </Layout>
    );
  }

  return (
    <Layout>
      <Preloader loading={fetching || updatingAccurate} />

      {/* Floating Alert for Unverified */}
      {!isVerified && !fetching && (
        <Box
          onClick={scrollToFeedback}
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#f59e0b",
            color: "white",
            padding: "12px 24px",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            cursor: "pointer",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Text weight={600}>Please verify if the data is accurate</Text>
        </Box>
      )}

      {/* Not Accurate Modal */}
      <Modal
        opened={showNotAccurateModal}
        onClose={() => setShowNotAccurateModal(false)}
        title={
          <Text size="lg" weight={700}>
            Mark as Not Accurate
          </Text>
        }
        centered
      >
        <Text mb={16}>
          Please provide a comment explaining what is incorrect:
        </Text>
        <Textarea
          placeholder="Describe what needs to be corrected..."
          value={notAccurateComment}
          onChange={(e) => setNotAccurateComment(e.currentTarget.value)}
          minRows={4}
          mb={20}
          required
        />
        <Flex justify="flex-end" gap={12}>
          <Button
            variant="outline"
            onClick={() => {
              setShowNotAccurateModal(false);
              setNotAccurateComment("");
            }}
          >
            Cancel
          </Button>
          <Button
            color="orange"
            onClick={handleSubmitNotAccurate}
            loading={updatingAccurate || addingComment}
            disabled={!notAccurateComment.trim()}
          >
            Submit
          </Button>
        </Flex>
      </Modal>

      {/* Confirm Accurate Modal */}
      <Modal
        opened={showConfirmAccurateModal}
        onClose={() => setShowConfirmAccurateModal(false)}
        title={
          <Text size="lg" weight={700}>
            Confirm Accuracy
          </Text>
        }
        centered
      >
        <Text mb={20}>
          Are you sure you want to mark this KYC result as accurate? This will
          disable the ability to add further comments.
        </Text>
        <Flex justify="flex-end" gap={12}>
          <Button
            variant="outline"
            onClick={() => setShowConfirmAccurateModal(false)}
          >
            Cancel
          </Button>
          <Button
            color="orange"
            onClick={handleConfirmAccurate}
            loading={updatingAccurate}
          >
            Confirm
          </Button>
        </Flex>
      </Modal>

      <Box className={classes.container}>
        <Flex className={classes.header} justify="space-between" align="center">
          <Box>
            <Title className={classes.headerTitle}>KYC Report</Title>
            <Text size="sm" mt={8}>
              KYC Report disiapkan untuk
            </Text>
            <Title order={2} mt={4}>
              {companyData?.company_identity?.name}
            </Title>
            <Text size="sm" mt={4}>
              Tanggal {formatDate(new Date().toISOString())}
            </Text>
          </Box>

          <Flex direction="column" align="flex-end" gap={8}>
            <Badge
              className={cx(classes.forgeryBadge, {
                [classes.forgeryTrue]: forgeryResult?.forgery,
              })}
            >
              {forgeryResult?.forgery
                ? "FORGERY INDICATION"
                : "NO FORGERY INDICATION"}
            </Badge>
            <Button
              leftIcon={<SvgDownload />}
              onClick={handleDownloadPDF}
              className={classes.printButton}
              sx={{
                "@media print": {
                  display: "none",
                },
              }}
            >
              Save as PDF
            </Button>
          </Flex>
        </Flex>

        {/* Company Summary */}
        <Box className={classes.section}>
          <Title className={classes.sectionTitle}>
            {companyData?.company_identity?.name}
          </Title>
          <Box className={classes.summaryBox}>
            <Text>{companyData?.company_summary}</Text>
          </Box>
        </Box>

        {/* Management Table */}
        <Box className={classes.section}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>Nama</th>
                <th>Jabatan</th>
                <th>Nominal</th>
                <th>Saham (%)</th>
              </tr>
            </thead>
            <tbody>
              {(companyData?.management ?? [])?.map((member, index) => (
                <tr key={index}>
                  <td>{member?.name}</td>
                  <td>{member?.position}</td>
                  <td>{member?.shares?.value}</td>
                  <td>{member?.shares?.percentage}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>

        {/* CLIK Results */}
        <Box className={classes.section}>
          <Title className={classes.sectionTitle}>
            Hasil Pemeriksaan CLIK:
          </Title>
          <Box className={classes.card}>
            {creditInfoList.length > 0 && (
              <>
                <Text>
                  • Jumlah Fasilitas Kredit Aktif:{" "}
                  {creditInfoList.reduce(
                    (acc, item) => acc + parseInt(item.creditCount),
                    0
                  )}
                </Text>
                <Text>
                  • Total Fasilitas Kredit:{" "}
                  {formatCurrency(
                    creditInfoList.reduce(
                      (acc, item) => acc + parseFloat(item.creditLimit),
                      0
                    )
                  )}
                </Text>
                <Text>
                  • Total Fasilitas Kredit yang sudah digunakan:{" "}
                  {formatCurrency(
                    creditInfoList.reduce(
                      (acc, item) => acc + parseFloat(item.debitBalance),
                      0
                    )
                  )}
                </Text>
                <Text>
                  • Total Fasilitas Kredit yang sedang jatuh tempo:{" "}
                  {formatCurrency(
                    creditInfoList.reduce(
                      (acc, item) => acc + parseFloat(item.overdue),
                      0
                    )
                  )}
                </Text>
                <Text>• Status Fasilitas Kredit saat ini: Lancar</Text>
              </>
            )}
            <Text size="sm" color="dimmed" mt={12} align="right">
              (Data ditarik tanggal {formatDate(new Date().toISOString())})
            </Text>
          </Box>
        </Box>

        {/* Appendix Section */}
        <Box className={classes.appendixSection}>
          <Title className={classes.appendixTitle}>Appendix</Title>

          {/* A. Legalitas Usaha */}
          <Flex gap={40} wrap="wrap">
            <Box style={{ flex: "1 1 45%" }}>
              <Text className={classes.appendixSubtitle}>
                A. Legalitas Usaha
              </Text>
              <table className={classes.appendixTable}>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Keterangan</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>NPWP</td>
                    <td>{companyData?.company_identity?.npwp}</td>
                  </tr>
                  <tr>
                    <td>NIB</td>
                    <td>{companyData?.company_identity?.nib}</td>
                  </tr>
                  <tr>
                    <td>Izin Usaha</td>
                    <td>{companyData?.company_identity?.investment_status}</td>
                  </tr>
                </tbody>
              </table>
            </Box>

            {/* B. Alamat dan Kontak */}
            <Box style={{ flex: "1 1 45%" }}>
              <Text className={classes.appendixSubtitle}>
                B. Alamat dan Kontak
              </Text>
              <table className={classes.appendixTable}>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Keterangan</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Alamat</td>
                    <td>{companyData?.company_identity?.address}</td>
                  </tr>
                  <tr>
                    <td>Telepon</td>
                    <td>{companyData?.company_identity?.no_telp}</td>
                  </tr>
                  <tr>
                    <td>Email</td>
                    <td>{companyData?.company_identity?.email}</td>
                  </tr>
                  <tr>
                    <td>Kode Pos</td>
                    <td>{companyData?.company_identity?.kode_pos}</td>
                  </tr>
                </tbody>
              </table>
            </Box>
          </Flex>

          {/* C. Akta Perusahaan */}
          <Box mt={24}>
            <Text className={classes.appendixSubtitle}>C. Akta Perusahaan</Text>
            <table className={classes.appendixTable}>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Keterangan</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Jenis Akta</td>
                  <td>Akta Pendirian</td>
                </tr>
                <tr>
                  <td>Nomor Akta</td>
                  <td>{companyData?.establishment_deed?.number}</td>
                </tr>
                <tr>
                  <td>Tanggal Akta</td>
                  <td>{companyData?.establishment_deed?.date}</td>
                </tr>
                <tr>
                  <td>Nama Notaris</td>
                  <td>{companyData?.establishment_deed?.notary}</td>
                </tr>
                <tr>
                  <td>Lokasi terbit</td>
                  <td>{companyData?.establishment_deed?.location}</td>
                </tr>
                <tr>
                  <td>Tanggal Pengesahan</td>
                  <td>{companyData?.establishment_deed?.ratification_date}</td>
                </tr>
                <tr>
                  <td>Keterangan</td>
                  <td>
                    Terdaftar di AHU -{" "}
                    {companyData?.establishment_deed?.ahu_number}
                  </td>
                </tr>
              </tbody>
            </table>

            <table className={classes.appendixTable} style={{ marginTop: 16 }}>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Keterangan</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Jenis Akta</td>
                  <td>Pernyataan Keputusan Para Pemegang Saham</td>
                </tr>
                <tr>
                  <td>Nomor Akta</td>
                  <td>{companyData?.latest_amendment?.number}</td>
                </tr>
                <tr>
                  <td>Tanggal Akta</td>
                  <td>{companyData?.latest_amendment?.date}</td>
                </tr>
                <tr>
                  <td>Nama Notaris</td>
                  <td>{companyData?.latest_amendment?.notary}</td>
                </tr>
                <tr>
                  <td>Lokasi terbit</td>
                  <td>{companyData?.latest_amendment?.location}</td>
                </tr>
                <tr>
                  <td>Tanggal Pengesahan</td>
                  <td>{companyData?.latest_amendment?.ratification_date}</td>
                </tr>
                <tr>
                  <td>Keterangan</td>
                  <td>
                    Terdaftar di AHU -{" "}
                    {companyData?.latest_amendment?.ahu_number}
                  </td>
                </tr>
              </tbody>
            </table>
          </Box>
        </Box>

        {/* D. Pengurus dan Pemegang Saham */}
        <Box className={classes.section} mt={24}>
          <Title className={classes.sectionTitle}>
            D. Pengurus dan Pemegang Saham
          </Title>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>Nama</th>
                <th>Jabatan</th>
                <th>Nominal Saham</th>
                <th>% Kepemilikan</th>
              </tr>
            </thead>
            <tbody>
              {(companyData?.management ?? []).map((member, index) => (
                <tr key={index}>
                  <td>{member?.name}</td>
                  <td>{member?.position}</td>
                  <td>{member?.shares?.value}</td>
                  <td>{member?.shares?.percentage}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>

        {/* E. Hasil Perbandingan Data */}
        <Box className={classes.section}>
          <Title className={classes.sectionTitle}>
            E. Hasil Perbandingan Data
          </Title>
          {(companyData.management ?? []).map((management, idx) => {
            const rows = [
              {
                label: "NIK",
                value: management?.nik,
                verified: management?.nik_verified,
                compared: management?.nik_compared,
              },
              {
                label: "Passport",
                value: management?.passport,
                verified: management?.passport_verified,
                compared: management?.passport_compared,
              },
              {
                label: "Passport Expiry Date",
                value: management?.passport_exp,
                verified: management?.passport_exp_verified,
                compared: management?.passport_exp_compared,
              },
              {
                label: "Tempat Tanggal Lahir",
                value: management?.birth_place_date,
                verified: management?.birth_verified,
                compared: management?.birth_compared,
              },
              {
                label: "Occupation",
                value: management?.occupation,
                verified: management?.occupation_verified,
                compared: management?.occupation_compared,
              },
              {
                label: "Address",
                value: management?.address,
                verified: management?.address_verified,
                compared: management?.address_compared,
              },
            ].filter((row) => row.value != null);

            if (rows.length === 0) return null;

            return (
              <Box key={idx} className={classes.card}>
                <Text weight={600} mb={12}>
                  {management?.name}
                </Text>
                <table className={classes.table}>
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Isi Data</th>
                      <th>Hasil</th>
                      <th>Keterangan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={index}>
                        <td>{row.label}</td>
                        <td>{row.value}</td>
                        <td>
                          {row.verified === true
                            ? "Sesuai"
                            : "Tidak Dapat Dibandingkan"}
                        </td>
                        <td>{row.compared}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            );
          })}
        </Box>

        {/* F. Pengurus dan Pemegang Saham Sebelumnya */}
        <Box className={classes.section}>
          <Title className={classes.sectionTitle}>
            F. Pengurus dan Pemegang Saham Sebelumnya
          </Title>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>Nama</th>
                <th>Jabatan</th>
                <th>Nominal Saham</th>
                <th>% Kepemilikan</th>
                <th>Referensi</th>
              </tr>
            </thead>
            <tbody>
              {(companyData?.past_management ?? []).map((member, index) => (
                <tr key={index}>
                  <td>{member?.name}</td>
                  <td>{member?.position}</td>
                  <td>{member?.shares?.value}</td>
                  <td>{member?.shares?.percentage}%</td>
                  <td>{member?.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>

        {/* G. Bidang Usaha */}
        <Box className={classes.section}>
          <Title className={classes.sectionTitle}>G. Bidang Usaha</Title>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>Nama</th>
                <th>Kode KBLI</th>
                <th>Referensi</th>
              </tr>
            </thead>
            <tbody>
              {(companyData?.business_fields?.main_fields ?? []).map(
                (field, index) => (
                  <tr key={index}>
                    <td>{field?.name}</td>
                    <td>{field?.kbli_code}</td>
                    <td>{field?.notes}</td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </Box>

        {/* H. Perubahan Modal Usaha */}
        <Box className={classes.section}>
          <Title className={classes.sectionTitle}>
            H. Perubahan Modal Usaha
          </Title>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>Jenis Modal</th>
                <th>Nominal Lama</th>
                <th>Nominal Baru</th>
                <th>Tanggal</th>
                <th>Deskripsi</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Modal Dasar</td>
                <td>
                  {
                    companyData?.significant_changes?.capital
                      ?.authorized_capital?.old_value
                  }
                </td>
                <td>
                  {
                    companyData?.significant_changes?.capital
                      ?.authorized_capital?.new_value
                  }
                </td>
                <td>{companyData?.latest_amendment?.date}</td>
                <td>
                  {
                    companyData?.significant_changes?.capital
                      ?.authorized_capital?.description
                  }
                </td>
              </tr>
              <tr>
                <td>Modal yang di setor</td>
                <td>
                  {
                    companyData?.significant_changes?.capital?.paid_up_capital
                      ?.old_value
                  }
                </td>
                <td>
                  {
                    companyData?.significant_changes?.capital?.paid_up_capital
                      ?.new_value
                  }
                </td>
                <td>{companyData?.latest_amendment?.date}</td>
                <td>
                  {
                    companyData?.significant_changes?.capital?.paid_up_capital
                      ?.description
                  }
                </td>
              </tr>
              <tr>
                <td>Peningkatan Modal yang disetor</td>
                <td></td>
                <td>
                  {companyData?.significant_changes?.capital?.new_shares?.value}
                </td>
                <td>{companyData?.latest_amendment?.date}</td>
                <td>
                  {
                    companyData?.significant_changes?.capital?.new_shares
                      ?.description
                  }
                </td>
              </tr>
            </tbody>
          </table>
        </Box>

        {/* I. Distribusi Profit */}
        <Box className={classes.section}>
          <Title className={classes.sectionTitle}>I. Distribusi Profit</Title>
          <Text mb={8}>
            Tahun {companyData?.significant_changes?.profit_distribution?.year}
          </Text>
          <Text mb={8}>
            Net Profit{" "}
            {companyData?.significant_changes?.profit_distribution?.net_profit}
          </Text>
          <Text weight={600} mb={8}>
            Alokasi
          </Text>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>Tujuan</th>
                <th>Nominal</th>
                <th>Presentase</th>
              </tr>
            </thead>
            <tbody>
              {companyData?.significant_changes?.profit_distribution?.allocations?.map(
                (allocation, index) => (
                  <tr key={index}>
                    <td>{allocation?.purpose}</td>
                    <td>{allocation?.amount}</td>
                    <td>{allocation?.percentage}%</td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </Box>

        {/* J. Perubahan Penting Lainnya */}
        <Box className={classes.section}>
          <Title className={classes.sectionTitle}>
            J. Perubahan Penting Lainnya
          </Title>
          <ul>
            {(companyData?.significant_changes?.others ?? [])?.map(
              (change, index) => (
                <li key={index}>
                  {change?.description} ({change?.sources})
                </li>
              )
            )}
          </ul>
        </Box>

        {/* K. Perbedaan Informasi antar Dokumen */}
        <Box className={classes.section}>
          <Title className={classes.sectionTitle}>
            K. Perbedaan Informasi antar Dokumen
          </Title>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>Perbedaan</th>
                <th>Deskripsi</th>
              </tr>
            </thead>
            <tbody>
              {(companyData?.document_discrepancies ?? []).map(
                (discrepancy, index) => (
                  <tr key={index}>
                    <td>{discrepancy?.type}</td>
                    <td>{discrepancy?.description}</td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </Box>

        {/* L. Riwayat Kredit */}
        <Box className={classes.section}>
          <Title className={classes.sectionTitle}>L. Riwayat Kredit</Title>
          {(creditInfoList ?? []).map((creditInfo, idx) => (
            <Box key={idx} className={classes.card}>
              <Text weight={600} mb={12}>
                {creditInfo?.tradeName}
              </Text>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Jenis Fasilitas</th>
                    <th>Nama Nasabah</th>
                    <th>Bank</th>
                    <th>Tanggal Awal Kredit</th>
                    <th>Tanggal Akhir Kredit</th>
                    <th>Tanggal Posisi Data</th>

                    <th>Limit Kredit</th>
                    <th>Saldo Debit</th>
                    <th>Limit Kredit Awal</th>
                    <th>Suku Bunga</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(creditInfo?.creditHistory ?? []).map((history, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{history?.usageType}</td>
                      <td>{creditInfo?.tradeName}</td>
                      <td>{history?.providerCode}</td>
                      <td>{history?.startDate}</td>
                      <td>{history?.dueDate}</td>
                      <td>{history?.referenceDate}</td>
                      <td>{formatCurrency(history?.creditLimit || 0)}</td>
                      <td>{formatCurrency(history?.debitBalance || 0)}</td>
                      <td>
                        {formatCurrency(history?.initialCreditLimit || 0)}
                      </td>
                      <td>{history?.interestRate}%</td>
                      <td>
                        <Badge
                          className={cx(
                            classes.statusBadge,
                            history?.status === "Lancar"
                              ? classes.statusLancar
                              : history?.status === "Tidak Lancar"
                              ? classes.statusTidakLancar
                              : classes.statusOverdue
                          )}
                        >
                          {history?.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          ))}
        </Box>

        {/* M. Litigasi dan Berita Negatif */}
        {negativeInfo && (
          <Box className={classes.section}>
            <Title className={classes.sectionTitle}>
              M. Litigasi dan Berita Negatif
            </Title>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Keterangan</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Sumber</td>
                  <td>Direktori Putusan Mahkamah Agung</td>
                </tr>
                <tr>
                  <td>Tanggal Pencarian</td>
                  <td>{formatDate(new Date().toISOString())}</td>
                </tr>
                <tr>
                  <td>Hasil</td>
                  <td>{negativeInfo?.negative_news}</td>
                </tr>
              </tbody>
            </table>

            <Title className={classes.sectionTitle} mt={24}>
              PEP Check
            </Title>
            {(negativeInfo?.management ?? [])?.map((member, index) => (
              <Box key={index} className={classes.card}>
                <Flex justify="space-between" align="center">
                  <Text weight={600}>{member?.name}</Text>
                  <Badge color={member?.is_pep ? "red" : "green"}>
                    {member?.is_pep ? "PEP" : "Not PEP"}
                  </Badge>
                </Flex>
                {member?.is_pep && member?.news && (
                  <Box mt={12}>
                    {member.news.map((newsItem, newsIdx) => (
                      <Box key={newsIdx} mb={8}>
                        <Text
                          component="a"
                          href={newsItem?.url}
                          target="_blank"
                          color="blue"
                          size="sm"
                        >
                          {newsItem?.title}
                        </Text>
                        <Text size="xs" color="dimmed">
                          {newsItem?.snippet}
                        </Text>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        <Box id="feedback-section" className={classes.section} mt={32}>
          <Title className={classes.sectionTitle}>Feedback</Title>
          <Text mb={16}>Apakah data sudah akurat?</Text>

          <Flex gap={12} mb={24}>
            <Button
              variant={isAccurate === true ? "filled" : "outline"}
              color="green"
              onClick={handleYesClick}
              loading={updatingAccurate}
              disabled={isAccurate === true}
            >
              Ya
            </Button>
            <Button
              variant={isAccurate === false ? "filled" : "outline"}
              color="red"
              onClick={handleNoClick}
              loading={updatingAccurate}
              disabled={isAccurate === false}
            >
              Tidak
            </Button>
          </Flex>
        </Box>

        {/* M. Comments Section */}
        <Box className={classes.commentSection}>
          <Title className={classes.sectionTitle}>M. Comments</Title>

          {isAccurate === true && (
            <Box
              mb={16}
              p={12}
              style={{
                backgroundColor: "#d1fae5",
                borderRadius: 8,
                border: "1px solid #10b981",
              }}
            >
              <Text size="sm" color="green">
                ✓ This result has been marked as accurate. Comments are
                disabled. Click Tidak above if you need to add corrections.
              </Text>
            </Box>
          )}

          {!isVerified && (
            <Box
              mb={16}
              p={12}
              style={{
                backgroundColor: "#fef3c7",
                borderRadius: 8,
                border: "1px solid #f59e0b",
              }}
            >
              <Text size="sm" color="orange">
                ⚠ Please verify if the data is accurate before adding comments.
              </Text>
            </Box>
          )}

          {/* Existing Comments */}
          {comments.length > 0 ? (
            comments.map((comment) => (
              <Box key={comment?.id} className={classes.commentItem}>
                <Flex justify="space-between" align="center">
                  <Text className={classes.commentAuthor}>
                    {comment?.commenter}
                  </Text>
                  <Text className={classes.commentTime}>
                    {formatDateTime(comment?.createdAt)}
                  </Text>
                </Flex>
                <Text className={classes.commentText}>{comment?.comment}</Text>
              </Box>
            ))
          ) : (
            <Text color="dimmed" mb={16}>
              No comments yet.
            </Text>
          )}

          {/* Add Comment - Only show if verified and not accurate */}
          {isVerified && isAccurate !== true && (
            <Box mt={20}>
              <Textarea
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.currentTarget.value)}
                minRows={3}
                mb={12}
              />
              <Flex justify="flex-end">
                <Button
                  onClick={handleAddComment}
                  loading={addingComment}
                  disabled={!newComment.trim()}
                  style={{
                    backgroundColor: "#1a2332",
                  }}
                >
                  Add Comment
                </Button>
              </Flex>
            </Box>
          )}

          {/* Disabled Comment Box - Show when not verified */}
          {!isVerified && (
            <Box mt={20}>
              <Textarea
                placeholder="Please verify the data accuracy first..."
                disabled
                minRows={3}
                mb={12}
                styles={{
                  input: {
                    backgroundColor: "#f3f4f6",
                    cursor: "not-allowed",
                  },
                }}
              />
              <Flex justify="flex-end">
                <Button
                  disabled
                  style={{
                    backgroundColor: "#ccc",
                  }}
                >
                  Add Comment
                </Button>
              </Flex>
            </Box>
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default KYCResultPage;
